import React, { useCallback, useMemo } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  IconButton,
  Link,
  Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles'
import { makeStyles } from '@Compat/makeStyles';
import { siteTokens } from "../siteTokens";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLinkedin } from '@fortawesome/free-brands-svg-icons/faLinkedin';
import { faXTwitter } from '@fortawesome/free-brands-svg-icons/faXTwitter';
import { faYoutube } from '@fortawesome/free-brands-svg-icons/faYoutube';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons/faEnvelope';
import { useHistory } from "react-router-dom";
import { IRoutes } from "../../../routesEnum";
import { useSiteContent } from "../SiteContentContext";

// "Become a partner" footer CTA label per marketing locale (the site uses 6
// locales, no uk). Kept inline so the link needs no content-pipeline change.
const PARTNER_CTA_LABEL: Record<string, string> = {
  en: "Become a partner",
  pt: "Torne-se parceiro",
  es: "Conviértete en socio",
  fr: "Devenir partenaire",
  de: "Partner werden",
  it: "Diventa partner",
};
import { isCalendlyHref, useDemoDrawer } from "../DemoDrawerContext";
import { trackSiteCta } from "../marketing";
import { track } from "../../../core/analytics/ga4";
import { isLegalDocHref, useLegalDrawer } from "../LegalDrawerContext";
import { useCookieConsent } from "@Components/consent/CookieConsentProvider";
// Recoloured to the selected theme (was a fixed cobalt → navy gradient).
import FidavaSymbolMark from "../assets/FidavaSymbolMark.svg?raw";
import { themedSvgUrl } from "../themedSvg";
import { scrollToSiteSection } from "../siteSections";

// Footer legal link that re-opens the cookie preferences dialog (consent
// withdrawal must be as easy as giving it — GDPR Art. 7(3)).
const COOKIE_SETTINGS_HREF = "#cookie-settings";

const useStyles = makeStyles((theme) => {
  const tk = siteTokens(theme);
  return {
  footer: {
    position: "relative",
    overflow: "hidden",
    marginTop: theme.spacing(12),
    padding: theme.spacing(8, 0, 4),
    // Fully opaque, distinctly different from the soft pastel sections
    // above so the footer reads as its own zone (the previous alpha-
    // blended pastel was visually indistinguishable from the section
    // backdrop, hence "no background").
    background:
      tk.dark
        ? `linear-gradient(180deg, ${tk.surfaceDeep} 0%, ${tk.surface} 100%)`
        : `linear-gradient(180deg, ${tk.accent2A(0.06)} 0%, ${tk.accentA(0.1)} 100%), ${tk.surfaceSoft}`,
    color: tk.ink,
    // Reserve room at the bottom on viewports where MobileStickyCta is
    // visible (<md). Without it, the legal row sits behind the sticky
    // gradient pill and visitors can't scroll past it on touch.
    [theme.breakpoints.down("md")]: {
      paddingBottom: theme.spacing(11)
    }
  },
  glow: {
    position: "absolute",
    width: 420,
    height: 420,
    borderRadius: "50%",
    background:
      `radial-gradient(circle, ${tk.accentA(tk.dark ? 0.38 : 0.24)} 0%, transparent 70%)`,
    filter: "blur(16px)",
    opacity: 0.6,
    pointerEvents: "none"
  },
  glowTop: {
    top: -160,
    right: -120
  },
  glowBottom: {
    bottom: -200,
    left: -140
  },
  // Large, faint brand mark bleeding off the right edge — decorative
  // only (aria-hidden), vertically centered on the footer. Ported from
  // the prototype's closing-section treatment (moved here from
  // FinalCta.tsx, whose own height varies with the pricing cards below
  // it and never let the mark actually read as centered).
  mark: {
    position: "absolute",
    top: "50%",
    right: "-5%",
    width: "min(46vw, 620px)",
    height: "auto",
    transform: "translateY(-50%)",
    objectFit: "contain",
    opacity: theme.palette.mode === "dark" ? 0.1 : 0.17,
    pointerEvents: "none",
    [theme.breakpoints.down("md")]: {
      display: "none"
    }
  },
  content: {
    position: "relative",
    zIndex: 1
  },
  ctaPanel: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(4),
    borderRadius: 28,
    backgroundColor:
      tk.surfaceA(tk.dark ? 0.72 : 0.78),
    border: `1px solid ${tk.accentA(0.13)}`,
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 32px 80px rgba(0, 0, 0, 0.38)"
        : `0 28px 72px ${tk.accentDeepA(0.16)}`,
    marginBottom: theme.spacing(6),
    // Row layout only at lg+ (1200). At md (900-1199) the row had
    // copy at 520px max + a 200px+ no-wrap button + padding — that
    // could squeeze the button against the right edge and clip the
    // label. Staying column up to lg gives the button its own row.
    [theme.breakpoints.up("lg")]: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between"
    }
  },
  ctaButton: {
    "&&": {
      padding: theme.spacing(1.6, 4.5),
      borderRadius: 999,
      fontWeight: 700,
      fontSize: "1.05rem",
      textTransform: "none",
      border: "1.5px solid transparent",
      color: tk.onAccent,
      background: tk.gradient(135),
      boxShadow: `0 18px 38px ${tk.accentDeepA(0.36)}`,
      transition: "transform 200ms ease, box-shadow 200ms ease",
      whiteSpace: "nowrap",
      "&:hover": {
        background: tk.gradient(135),
        transform: "translateY(-2px)",
        boxShadow: `0 26px 52px ${tk.accentDeepA(0.48)}`
      }
    }
  },
  ctaText: {
    maxWidth: 520,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1)
  },
  navHeadline: {
    fontWeight: 600,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    marginBottom: theme.spacing(2)
  },
  navList: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(1.2)
  },
  navLink: {
    color: tk.dark ? tk.inkA(0.85) : tk.inkSoft,
    textDecoration: "none",
    fontSize: "0.95rem",
    // Link renders as a <button> for sections — the user-agent
    // default text-align on buttons is center, so the link copy
    // drifted to the centre of each column while the heading stayed
    // left. Pin the alignment so headings and links agree.
    textAlign: "left",
    transition: theme.transitions.create(["color", "transform"], {
      duration: theme.transitions.duration.shorter
    }),
    "&:hover": {
      color: tk.accent,
      transform: "translateX(4px)"
    }
  },
  contactRow: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5)
  },
  socialRow: {
    display: "flex",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2)
  },
  divider: {
    margin: theme.spacing(6, 0, 3),
    borderColor: tk.border
  },
  legalRow: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    [theme.breakpoints.up("sm")]: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center"
    }
  },
  legalLinks: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap"
  },
  legalLink: {
    color: tk.dark ? tk.inkA(0.75) : tk.inkSoft,
    textDecoration: "none",
    fontSize: "0.9rem",
    "&:hover": {
      color: tk.accent
    }
  }
  };
});

const SOCIAL_ICON_MAP: Record<string, React.ReactNode> = {
  linkedin: <FontAwesomeIcon icon={faLinkedin} />,
  twitter: <FontAwesomeIcon icon={faXTwitter} />,
  youtube: <FontAwesomeIcon icon={faYoutube} />
};

const Footer: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { content, locale } = useSiteContent();
  const { open: openDemoDrawer } = useDemoDrawer();
  const { open: openLegalDrawer } = useLegalDrawer();
  const { openPreferences: openCookiePreferences } = useCookieConsent();
  const currentYear = new Date().getFullYear();

  const footerContent = content.footer ?? {};
  const cta = footerContent.cta;
  const brand = footerContent.brand ?? {};
  const socialLinks = Array.isArray(footerContent.social) ? footerContent.social : [];
  const navigation = footerContent.navigation ?? {};
  const productNavItems = navigation.product?.items;
  const productSections = Array.isArray(productNavItems) ? productNavItems : [];
  const resourceNavItems = navigation.resources?.items;
  const resourceLinks = Array.isArray(resourceNavItems) ? resourceNavItems : [];
  const statusNavLinks = navigation.status?.links;
  const statusLinks = Array.isArray(statusNavLinks) ? statusNavLinks : [];
  const legal = footerContent.legal ?? {};
  const legalLinks = Array.isArray(legal.links) ? legal.links : [];
  const legalBaseText = legal.text;
  const legalLocaleLabel = legal.localeLabel;

  const ctaButtonHref = cta?.button?.href ?? IRoutes.wizard;
  const ctaButtonLabel = cta?.button?.label;
  const isCtaExternal = Boolean(ctaButtonHref && /^(?:[a-z][a-z0-9+.-]*:)/i.test(ctaButtonHref));

  const productTitle = navigation.product?.title;
  const resourcesTitle = navigation.resources?.title;
  const statusTitle = navigation.status?.title;
  const statusDescription = navigation.status?.description;

  const localeDisplay = typeof locale === "string" ? locale.toUpperCase() : undefined;
  const legalText = useMemo(() => {
    if (!legalBaseText) {
      return undefined;
    }
    const tokenLocale = localeDisplay ?? legalLocaleLabel ?? "";
    return legalBaseText
      .replace(/\{\{year\}\}/g, String(currentYear))
      .replace(/\{\{locale\}\}/g, tokenLocale);
  }, [currentYear, legalBaseText, legalLocaleLabel, localeDisplay]);

  const handleSectionNavigate = useCallback((section: string) => {
    if (!section) {
      return;
    }
    scrollToSiteSection(section);
  }, []);

  const handleRouteNavigate = useCallback(
    (path: string) => {
      if (!path) {
        return;
      }
      if (path.startsWith("/")) {
        history.push(path);
      }
    },
    [history]
  );

  const isExternalHref = useCallback((href?: string) => {
    if (!href) {
      return false;
    }
    return /^(?:[a-z][a-z0-9+.-]*:)/i.test(href);
  }, []);

  return (
    <footer className={classes.footer}>
      <span className={`${classes.glow} ${classes.glowTop}`} />
      <span className={`${classes.glow} ${classes.glowBottom}`} />
      <img className={classes.mark} src={themedSvgUrl(FidavaSymbolMark, theme)} alt="" aria-hidden="true" />
      <Container maxWidth="xl" className={classes.content}>
        <Box className={classes.ctaPanel}>
          <Box className={classes.ctaText}>
            {cta?.eyebrow ? (
              <Typography variant="overline" color="primary">
                {cta.eyebrow}
              </Typography>
            ) : null}
            {cta?.title ? (
              <Typography variant="h5" component="p">
                {cta.title}
              </Typography>
            ) : null}
            {cta?.description ? (
              <Typography variant="body2" color={theme.palette.mode === "dark" ? "textSecondary" : "textSecondary"}>
                {cta.description}
              </Typography>
            ) : null}
          </Box>
          {ctaButtonLabel ? (
            <Button
              variant="contained"
              color="primary"
              size="large"
              className={classes.ctaButton}
              component={isCtaExternal ? "a" : "button"}
              href={isCtaExternal ? ctaButtonHref : undefined}
              target={isCtaExternal && !isCalendlyHref(ctaButtonHref) ? "_blank" : undefined}
              rel={isCtaExternal && !isCalendlyHref(ctaButtonHref) ? "noopener noreferrer" : undefined}
              onClick={(e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
                trackSiteCta(ctaButtonHref, "footer");
                if (isCalendlyHref(ctaButtonHref)) {
                  e.preventDefault();
                  openDemoDrawer(ctaButtonHref);
                  return;
                }
                if (!isCtaExternal) {
                  handleRouteNavigate(ctaButtonHref);
                }
              }}
            >
              {ctaButtonLabel}
            </Button>
          ) : null}
        </Box>

        <Grid container spacing={6}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            {brand.name ? <Typography variant="h6" component="p">{brand.name}</Typography> : null}
            {brand.description ? (
              <Typography variant="body2" color="textSecondary" paragraph>
                {brand.description}
              </Typography>
            ) : null}
            <Box className={classes.contactRow}>
              {brand.email ? (
                <Link
                  href={`mailto:${brand.email}`}
                  color="inherit"
                  underline="none"
                  onClick={() => track("contact_click", { method: "email", location: "footer" })}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    style={{ gap: theme.spacing(1) }}
                  >
                    <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: 20 }} />
                    <Typography variant="body2">{brand.emailLabel ?? brand.email}</Typography>
                  </Box>
                </Link>
              ) : null}
              {socialLinks.length ? (
                <Box className={classes.socialRow}>
                  {socialLinks.map((item) => {
                    if (!item?.href) {
                      return null;
                    }
                    const iconNode = SOCIAL_ICON_MAP[item.id ?? ""];
                    if (!iconNode) {
                      return null;
                    }
                    return (
                      <IconButton
                        key={item.id ?? item.href}
                        aria-label={item.label ?? item.id ?? "social"}
                        color="inherit"
                        size="small"
                        component="a"
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {iconNode}
                      </IconButton>
                    );
                  })}
                </Box>
              ) : null}
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            {productTitle ? (
              <Typography variant="subtitle2" component="h2" className={classes.navHeadline}>
                {productTitle}
              </Typography>
            ) : null}
            <div className={classes.navList}>
              {productSections.map((item) => {
                if (!item?.label) {
                  return null;
                }
                return (
                  <Link
                    key={`${item.label}-${item.section ?? ""}`}
                    component="button"
                    className={classes.navLink}
                    onClick={() => handleSectionNavigate(item.section ?? "")}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            {resourcesTitle ? (
              <Typography variant="subtitle2" component="h2" className={classes.navHeadline}>
                {resourcesTitle}
              </Typography>
            ) : null}
            <div className={classes.navList}>
              {resourceLinks.map((item) => {
                if (!item?.label) {
                  return null;
                }
                if (item.section) {
                  return (
                    <Link
                      key={`${item.label}-${item.section}`}
                      component="button"
                      className={classes.navLink}
                      onClick={() => handleSectionNavigate(item.section ?? "")}
                    >
                      {item.label}
                    </Link>
                  );
                }
                if (item.href) {
                  const external = isExternalHref(item.href);
                  return (
                    <Link
                      key={`${item.label}-${item.href}`}
                      className={classes.navLink}
                      href={external ? item.href : undefined}
                      target={external ? "_blank" : undefined}
                      rel={external ? "noopener noreferrer" : undefined}
                      component={external ? "a" : "button"}
                      onClick={external ? undefined : () => handleRouteNavigate(item.href ?? "")}
                    >
                      {item.label}
                    </Link>
                  );
                }
                return null;
              })}
            </div>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            {statusTitle ? (
              <Typography variant="subtitle2" component="h2" className={classes.navHeadline}>
                {statusTitle}
              </Typography>
            ) : null}
            <div className={classes.navList}>
              {statusDescription ? (
                <Typography variant="body2" color="textSecondary">
                  {statusDescription}
                </Typography>
              ) : null}
              {statusLinks.map((item) => {
                if (!item?.label || !item.href) {
                  return null;
                }
                const external = isExternalHref(item.href);
                return (
                  <Link
                    key={`${item.label}-${item.href}`}
                    className={classes.navLink}
                    href={external ? item.href : undefined}
                    target={external ? "_blank" : undefined}
                    rel={external ? "noopener noreferrer" : undefined}
                    component={external ? "a" : "button"}
                    onClick={external ? undefined : () => handleRouteNavigate(item.href ?? "")}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </Grid>
        </Grid>

        <Divider className={classes.divider} />
        <Box className={classes.legalRow}>
          {legalText ? (
            <Typography variant="body2" color="textSecondary">
              {legalText}
            </Typography>
          ) : null}
          <div className={classes.legalLinks}>
            {/* Discoverability for the partner (reseller) program — routes to
                the public application page. */}
            <Link
              className={classes.legalLink}
              component="button"
              onClick={() => {
                track("partner_click", { location: "footer" });
                handleRouteNavigate(IRoutes.becomePartner);
              }}
            >
              {PARTNER_CTA_LABEL[locale] ?? PARTNER_CTA_LABEL.en}
            </Link>
            {legalLinks.map((item) => {
              if (!item?.label || !item.href) {
                return null;
              }
              // "Cookie settings" re-opens the consent preferences dialog so
              // the user can withdraw or change consent at any time.
              if (item.href === COOKIE_SETTINGS_HREF) {
                return (
                  <Link
                    key={`${item.label}-${item.href}`}
                    className={classes.legalLink}
                    component="button"
                    onClick={() => openCookiePreferences()}
                  >
                    {item.label}
                  </Link>
                );
              }
              // Legal agreements (/legal/*.md) open in the in-page
              // drawer with rendered markdown rather than navigating.
              if (isLegalDocHref(item.href)) {
                return (
                  <Link
                    key={`${item.label}-${item.href}`}
                    className={classes.legalLink}
                    component="button"
                    onClick={() => openLegalDrawer(item.href ?? "", item.label ?? "")}
                  >
                    {item.label}
                  </Link>
                );
              }
              const external = isExternalHref(item.href);
              return (
                <Link
                  key={`${item.label}-${item.href}`}
                  className={classes.legalLink}
                  component={external ? "a" : "button"}
                  href={external ? item.href : undefined}
                  target={external ? "_blank" : undefined}
                  rel={external ? "noopener noreferrer" : undefined}
                  onClick={external ? undefined : () => handleRouteNavigate(item.href ?? "")}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>
        </Box>
      </Container>
    </footer>
  );
};

export default Footer;
